import {
    Invoice,
    InvoiceAcceptance,
    InvoiceBuilderColumn,
    InvoiceBuilderFooterBlock,
    InvoiceBuilderParty,
    InvoiceImageWidth,
    InvoiceLine,
    InvoiceSignatureState,
    PaperSize,
} from '../types';
import {
    formatAcceptedAt,
    formatCustomValue,
    formatIsoDate,
    formatMinor,
    getBillToCustomValue,
    getCustomValue,
    invoiceNotes,
    invoiceSignatureState,
    normalizePaperSize,
    signatureImageUrl,
    signatureMethodLabel,
    signatureStatusLabel,
    visibleBillToFields,
    visibleColumns,
} from '../helpers';

export interface InvoicePdfTextSpan {
    key: string;
    text: string;
    bold?: boolean;
    italic?: boolean;
    href?: string;
}

export interface InvoicePdfLineRow {
    key: string;
    cells: string[];
}

export interface InvoicePdfModel {
    paperSize: PaperSize;
    pdfPageSize: 'A4' | 'LETTER';
    theme: Invoice['document']['design']['theme'];
    accentColor: string;
    isVoid: boolean;
    fontFamily: 'Helvetica' | 'Times-Roman';
    boldFontFamily: 'Helvetica-Bold' | 'Times-Bold';
    italicFontFamily: 'Helvetica-Oblique' | 'Times-Italic';
    title: string;
    numberLine: string;
    logoStyle: Invoice['document']['design']['logo']['style'];
    logoSize?: InvoiceImageWidth;
    logoImageUrl: string;
    wordmark: string;
    sellerName: string;
    sellerLines: string[];
    billToLabel: string;
    buyerName: string;
    buyerDetails: string[];
    issuedOnLabel: string;
    issuedOn: string;
    dueOnLabel: string;
    dueOn: string;
    showIssuedOn: boolean;
    showDueOn: boolean;
    columnLabels: string[];
    columnAligns: Array<'left' | 'right'>;
    columnKinds: Array<InvoiceBuilderColumn['kind']>;
    columnWidths: number[];
    rows: InvoicePdfLineRow[];
    subtotal: string;
    fees: { key: string; label: string; amount: string }[];
    total: string;
    note: string;
    footerBlocks: InvoiceBuilderFooterBlock[];
    signatureState: InvoiceSignatureState | 'none';
    signatureStatus: string;
    signatureAgreement: string;
    signatureImageUrl: string;
    signatureName: string;
    signatureMethod: string;
    signatureDate: string;
}

const partyName = (party: InvoiceBuilderParty) => (
    (party.tradingName || party.legalName || '').trim()
);

const partyLines = (party: InvoiceBuilderParty) => {
    const { address } = party;
    const legal = party.tradingName && party.legalName && party.tradingName !== party.legalName
        ? party.legalName
        : '';
    const location = [address.city, address.region, address.postalCode].filter(Boolean).join(', ');
    return [
        legal,
        address.line1,
        address.line2,
        location,
        address.countryCode,
        party.registrationNumber,
        party.contact.email,
        party.contact.phone,
        party.contact.website,
    ].filter(Boolean);
};

const visibleCustomerFields = (
    fields: Invoice['document']['design']['customerFields'],
) => new Set(fields?.includes('name') ? fields : []);

const cellValue = (
    column: InvoiceBuilderColumn,
    line: InvoiceLine,
    currency: string,
    locale: string,
) => {
    if (column.kind === 'description') return line.description;
    if (column.kind === 'quantity') return line.quantity;
    if (column.kind === 'rate') return formatMinor(line.rateMinor, currency, locale);
    if (column.kind === 'amount') return formatMinor(line.amounts.totalMinor, currency, locale);
    if (column.kind !== 'custom') return '';
    return formatCustomValue(getCustomValue(line.customValues, column.id), column.valueKind);
};

const alignForKind = (kind: InvoiceBuilderColumn['kind']): 'left' | 'right' => (
    kind === 'amount' || kind === 'rate' || kind === 'quantity' ? 'right' : 'left'
);

const QUANTITY_WIDTH = 26;
const MONEY_WIDTH = 56;
const CUSTOM_WIDTH = 80;
const DESCRIPTION_MIN_WIDTH = 72;

export const columnWidths = (
    kinds: Array<InvoiceBuilderColumn['kind']>,
    contentWidth: number,
) => {
    const reserved = kinds.reduce((sum, kind) => {
        if (kind === 'quantity') return sum + QUANTITY_WIDTH;
        if (kind === 'rate' || kind === 'amount') return sum + MONEY_WIDTH;
        if (kind === 'custom') return sum + CUSTOM_WIDTH;
        if (kind === 'description') return sum + DESCRIPTION_MIN_WIDTH;
        return sum;
    }, 0);
    const extra = contentWidth - reserved;
    const scale = extra < 0 ? contentWidth / reserved : 1;
    const descriptionExtra = extra > 0
        ? extra / Math.max(kinds.filter((kind) => kind === 'description').length, 1)
        : 0;
    return kinds.map((kind) => {
        if (kind === 'quantity') return QUANTITY_WIDTH * scale;
        if (kind === 'rate' || kind === 'amount') return MONEY_WIDTH * scale;
        if (kind === 'custom') return CUSTOM_WIDTH * scale;
        if (kind === 'description') return (DESCRIPTION_MIN_WIDTH + descriptionExtra) * scale;
        return 40 * scale;
    });
};

export const parseMarkdownSpans = (markdown: string): InvoicePdfTextSpan[][] => (
    markdown.split('\n').map((line, lineIndex) => {
        const spans: InvoicePdfTextSpan[] = [];
        const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|\[(.+?)\]\((https?:\/\/[^\s)]+)\))/g;
        let cursor = 0;
        let match = pattern.exec(line);
        while (match) {
            if (match.index > cursor) {
                spans.push({
                    key: `${lineIndex}-${cursor}`,
                    text: line.slice(cursor, match.index),
                });
            }
            if (match[2]) {
                spans.push({ key: `${lineIndex}-b-${cursor}`, text: match[2], bold: true });
            } else if (match[3]) {
                spans.push({ key: `${lineIndex}-i-${cursor}`, text: match[3], italic: true });
            } else {
                spans.push({ key: `${lineIndex}-a-${cursor}`, text: match[4], href: match[5] });
            }
            cursor = match.index + match[0].length;
            match = pattern.exec(line);
        }
        if (cursor < line.length) {
            spans.push({ key: `${lineIndex}-${cursor}`, text: line.slice(cursor) });
        }
        return spans.length > 0 ? spans : [{ key: `${lineIndex}-empty`, text: '' }];
    })
);

export const getInvoicePdfModel = (
    invoice: Invoice,
    acceptance: InvoiceAcceptance | null = null,
): InvoicePdfModel => {
    const { document: doc } = invoice;
    const { design } = doc;
    const columns = visibleColumns(design.columns);
    const locale = design.regional.locale || 'en-US';
    const pdfPageSize = normalizePaperSize(design.regional.paperSize) === 'Letter' ? 'LETTER' : 'A4';
    const contentWidth = (pdfPageSize === 'LETTER' ? 612 : 595.28)
        - (design.theme === 'minimal' ? 72 : 84);
    const currency = doc.totals.currency || design.regional.currency || 'USD';
    const numberLabel = (design.labels.invoiceNumber || '').trim();
    const invoiceNumber = (invoice.number || '').trim();
    const classic = design.theme === 'classic';
    const customerFields = visibleCustomerFields(design.customerFields);
    const metadataFields = design.metadataFields ?? ['issuedOn', 'dueOn'];
    const signatureState = invoiceSignatureState(invoice, acceptance);
    const extras = visibleBillToFields(design.billToFields).map((field) => {
        const formatted = formatCustomValue(
            getBillToCustomValue(doc.billToCustomValues, field.id),
            field.valueKind,
        );
        return formatted ? `${field.label} · ${formatted}` : '';
    }).filter(Boolean);
    const buyerContact = [
        customerFields.has('phone') ? doc.buyer.phone : '',
        customerFields.has('email') ? doc.buyer.email : '',
    ].filter(Boolean);

    return {
        paperSize: normalizePaperSize(design.regional.paperSize),
        pdfPageSize,
        theme: design.theme,
        accentColor: design.accentColor || '#0f172a',
        isVoid: invoice.status === 'void',
        fontFamily: classic ? 'Times-Roman' : 'Helvetica',
        boldFontFamily: classic ? 'Times-Bold' : 'Helvetica-Bold',
        italicFontFamily: classic ? 'Times-Italic' : 'Helvetica-Oblique',
        title: design.labels.documentTitle || 'Invoice',
        numberLine: [numberLabel, invoiceNumber].filter(Boolean).join(' · '),
        logoStyle: design.logo.style,
        logoSize: design.logo.size,
        logoImageUrl: design.logo.imageUrl,
        wordmark: design.logo.text,
        sellerName: design.showSellerDetails ? partyName(design.seller) : '',
        sellerLines: design.showSellerDetails ? partyLines(design.seller) : [],
        billToLabel: design.labels.billTo || 'Bill to',
        buyerName: customerFields.has('name') ? doc.buyer.name : '',
        buyerDetails: [...buyerContact, ...extras],
        issuedOnLabel: design.labels.issuedOn || 'Issue date',
        issuedOn: formatIsoDate(invoice.issuedOn, design.regional),
        dueOnLabel: design.labels.dueOn || 'Due date',
        dueOn: formatIsoDate(invoice.dueOn, design.regional),
        showIssuedOn: metadataFields.includes('issuedOn'),
        showDueOn: metadataFields.includes('dueOn'),
        columnLabels: columns.map((column) => column.label),
        columnAligns: columns.map((column) => alignForKind(column.kind)),
        columnKinds: columns.map((column) => column.kind),
        columnWidths: columnWidths(columns.map((column) => column.kind), contentWidth),
        rows: doc.lines.length > 0
            ? doc.lines.map((line) => ({
                key: line.id,
                cells: columns.map((column) => cellValue(column, line, currency, locale)),
            }))
            : [{ key: 'empty', cells: ['No line items'] }],
        subtotal: formatMinor(doc.totals.subtotalMinor, currency, locale),
        fees: (doc.totals.fees || []).map((fee) => {
            const rate = fee.calculation.type === 'percentage'
                ? ` (${fee.calculation.rateBasisPoints / 100}%)`
                : '';
            return {
                key: fee.id,
                label: `${fee.label}${rate}`,
                amount: formatMinor(fee.amountMinor, currency, locale),
            };
        }),
        total: formatMinor(doc.totals.totalMinor, currency, locale),
        note: invoiceNotes(invoice),
        footerBlocks: [...(design.footerBlocks || [])]
            .sort((left, right) => left.order - right.order),
        signatureState,
        signatureStatus: signatureStatusLabel(signatureState, acceptance),
        signatureAgreement: design.signatureRequest?.agreement?.trim() || '',
        signatureImageUrl: signatureImageUrl(acceptance),
        signatureName: acceptance?.name?.trim() || '',
        signatureMethod: signatureMethodLabel(acceptance),
        signatureDate: acceptance
            ? formatAcceptedAt(acceptance.acceptedAt, locale)
            : '',
    };
};
